import React, { useEffect, useRef, useState } from "react";
import "./Form.css";
import LoadingSpinner from "../LoadingSpinner";
import { modernStyles } from "./FormStyles";
import { FormField, FormProps } from "./FormTypes";
import {
  useInitialValues,
  useListOptions,
  useVisibility,
  handleUpdate,
  handleSave,
} from "./FormHooks";
import PhoneInput from "./Components/PhoneInput";
import ListDropdown from "./Components/ListDropdown";
import DateField from "./Components/DateField";
import { toBool } from "./Utility";
import MessageTile from "../MessageTile";
import { useUser } from "../../Context/UserContext";
import PeoplePicker from "./Components/PBSPeoplePicker";
import FieldWrapper from "./Components/FieldWrapper";
import PrioritizedListPicker from "./Components/PrioritizedListPicker";

const buttonContainerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px",
  padding: "16px 15px",
  marginTop: "24px",
  borderTop: "1px solid hsl(var(--border))",
};

const Form = (props: FormProps) => {
  const siteUrl = "https://sharepoint.teampbs.com";
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<
    "info" | "success" | "error" | "warning"
  >("info");
  const [reloading, setReloading] = useState(false);

  const [viewportWidth, setViewportWidth] = useState<number | null>(null);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== "undefined") {
        setViewportWidth(window.innerWidth);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // init values and visibility
  const [formValues, setFormValues] = useInitialValues(props.fields);
  const visibleFields = useVisibility(props.fields, formValues);

  // County options depend on the State field's *current* selection, not the
  // value on the user's stored profile - fall back to the profile value only
  // until the State field has a value of its own (e.g. forms with a County
  // field but no State field to select from).
  const stateField = props.fields.find((f) => f.listName === "State");
  const liveState =
    (stateField ? formValues[stateField.id] : undefined) ?? user?.State;

  // list options (deduped + StrictMode safe)
  const { fieldOptions, isLoading } = useListOptions(
    siteUrl,
    props.fields,
    liveState,
  );

  const handleFieldChange = (fieldId: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [fieldId]: value }));
    // Keep the source field object in sync so parent-level Update works
    const f = props.fields.find((x) => x.id === fieldId);
    if (f) (f as any).value = value;
  };

  // Clear any County selection once the user picks a different State - a
  // county carried over from the previous state is never a valid option.
  const previousStateRef = useRef(liveState);
  useEffect(() => {
    if (previousStateRef.current === liveState) return;
    previousStateRef.current = liveState;

    props.fields
      .filter((f) => f.listName === "County")
      .forEach((f) => handleFieldChange(f.id, ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveState]);

  const getOptionsForField = (field: FormField) => {
    if (field.options?.length) {
      if (typeof (field.options as any[])[0] === "object") {
        return field.options as any[];
      }
      return (field.options as string[])
        .map((t) => ({ key: t, text: t }))
        .sort((a, b) => a.text.localeCompare(b.text)); // Sort alphabetically
    }
    return fieldOptions[field.id] || [];
  };

  const groupFieldsIntoRows = (fields: FormField[], maxColumns: number) => {
    const v = fields.filter((f) => f.visible);
    const rows: FormField[][] = [];
    let current: FormField[] = [];
    for (let i = 0; i < v.length; i++) {
      const f = v[i];
      if (f.type === "Label") {
        if (current.length) rows.push(current);
        rows.push([f]);
        current = [];
      } else {
        current.push(f);
        if (current.length === maxColumns || i === v.length - 1) {
          rows.push(current);
          current = [];
        }
      }
    }
    return rows;
  };

  const baseColumns = props.columns ?? 1;
  let effectiveColumns = baseColumns;

  if (viewportWidth !== null) {
    if (viewportWidth < 768) {
      // Up to small phones and small tablets: 1 column
      effectiveColumns = 1;
    } else if (viewportWidth < 1024) {
      // 768px – 1023px: tablet = 2 columns max
      effectiveColumns = Math.min(2, baseColumns);
    } else if (viewportWidth < 2560) {
      // 1024px – 2559px: laptops / desktops = 4 columns max
      effectiveColumns = Math.min(3, baseColumns);
    } else {
      // 2560px and above: very large screens = 4 columns max
      effectiveColumns = Math.min(4, baseColumns);
    }
  }

  const fieldRows = groupFieldsIntoRows(visibleFields, effectiveColumns);

  const renderField = (field: FormField) => {
    const value = formValues[field.id];

    switch (field.type) {
      case "Label":
        return (
          <div
            key={field.id}
            className="form-section-label"
            dangerouslySetInnerHTML={{ __html: field.title }}
            style={field.style}
          />
        );

      case "Text": {
        return (
          <FieldWrapper
            key={field.id}
            fieldId={field.id}
            label={field.title}
            description={field.description}
            required={field.required}
            error={field.error}
            characterLimit={field.characterLimit}
            currentCharacterCount={(value ?? "").toString().length}
          >
            {field.multiline ? (
              <textarea
                id={field.id}
                className="form-textarea"
                placeholder={
                  field.id === "certification#"
                    ? (() => {
                        const analyst = formValues["analystLevel"] as any;
                        const al = String(
                          typeof analyst === "object"
                            ? (analyst?.text ?? analyst?.key ?? analyst?.value)
                            : (analyst ?? ""),
                        ).toUpperCase();
                        const prefix =
                          al === "RBT"
                            ? "RBT-"
                            : al === "BCBA" || al === "BCBA-D"
                              ? "1-"
                              : al === "BCABA"
                                ? "0-"
                                : "";
                        return prefix ? `${prefix}12-3456` : field.placeholder;
                      })()
                    : field.placeholder
                }
                readOnly={field.readOnly}
                required={
                  field.id === "certification#"
                    ? (() => {
                        const analyst = formValues["analystLevel"] as any;
                        if (
                          analyst === "BCBA" ||
                          analyst === "BCBA-D" ||
                          analyst === "RBT" ||
                          analyst === "BCaBA"
                        ) {
                          return true;
                        }
                        return field.required;
                      })()
                    : field.required
                }
                maxLength={field.maxlength}
                value={value ?? ""}
                onFocus={() => {
                  if (field.id === "applicantBACBID") {
                    const s = String(value ?? "").trim();
                    if (!s) {
                      handleFieldChange(field.id, "BACB");
                    }
                  } else if (field.id === "certification#") {
                    const analyst = formValues["analystLevel"] as any;
                    const al = String(
                      typeof analyst === "object"
                        ? (analyst?.text ?? analyst?.key ?? analyst?.value)
                        : (analyst ?? ""),
                    ).toUpperCase();
                    const prefix =
                      al === "RBT"
                        ? "RBT-"
                        : al === "BCBA" || al === "BCBA-D"
                          ? "1-"
                          : al === "BCABA"
                            ? "0-"
                            : "";
                    const s = String(value ?? "").trim();
                    if (!s && prefix) {
                      handleFieldChange(field.id, prefix);
                    }
                  }
                }}
                onChange={(e) => {
                  let v = String(e.target.value ?? "");
                  if (field.id === "applicantBACBID") {
                    v = v.toUpperCase();
                    const digits = v
                      .replace(/^BACB/i, "")
                      .replace(/\D+/g, "")
                      .slice(0, 6);
                    v = `BACB${digits}`;
                  } else if (field.id === "certification#") {
                    v = v.toUpperCase();
                    const analyst = formValues["analystLevel"] as any;
                    const al = String(
                      typeof analyst === "object"
                        ? (analyst?.text ?? analyst?.key ?? analyst?.value)
                        : (analyst ?? ""),
                    ).toUpperCase();
                    const prefix =
                      al === "RBT"
                        ? "RBT-"
                        : al === "BCBA" || al === "BCBA-D"
                          ? "1-"
                          : al === "BCABA"
                            ? "0-"
                            : "";
                    if (prefix) {
                      const afterPrefix = v.startsWith(prefix)
                        ? v.slice(prefix.length)
                        : v;
                      const digits = afterPrefix.replace(/\D+/g, "");
                      const part1 = digits.slice(0, 2);
                      const part2 = digits.slice(2);
                      v = prefix + part1 + (part2 ? "-" + part2 : "");
                    } else {
                      const digits = v.replace(/\D+/g, "");
                      v = digits;
                    }
                  }
                  handleFieldChange(field.id, v);
                }}
              />
            ) : (
              <input
                id={field.id}
                className="form-input"
                type="text"
                placeholder={
                  field.id === "certification#"
                    ? (() => {
                        const analyst = formValues["analystLevel"] as any;
                        const al = String(
                          typeof analyst === "object"
                            ? (analyst?.text ?? analyst?.key ?? analyst?.value)
                            : (analyst ?? ""),
                        ).toUpperCase();
                        const prefix =
                          al === "RBT"
                            ? "RBT-"
                            : al === "BCBA" || al === "BCBA-D"
                              ? "1-"
                              : al === "BCABA"
                                ? "0-"
                                : "";
                        return prefix ? `${prefix}12-3456` : field.placeholder;
                      })()
                    : field.placeholder
                }
                readOnly={field.readOnly}
                required={
                  field.id === "certification#"
                    ? (() => {
                        const analyst = formValues["analystLevel"] as any;
                        if (
                          analyst === "BCBA" ||
                          analyst === "BCBA-D" ||
                          analyst === "RBT" ||
                          analyst === "BCaBA"
                        ) {
                          return true;
                        }
                        return field.required;
                      })()
                    : field.required
                }
                maxLength={field.maxlength}
                value={value ?? ""}
                onFocus={() => {
                  if (field.id === "applicantBACBID") {
                    const s = String(value ?? "").trim();
                    if (!s) {
                      handleFieldChange(field.id, "BACB");
                    }
                  } else if (field.id === "certification#") {
                    const analyst = formValues["analystLevel"] as any;
                    const al = String(
                      typeof analyst === "object"
                        ? (analyst?.text ?? analyst?.key ?? analyst?.value)
                        : (analyst ?? ""),
                    ).toUpperCase();
                    const prefix =
                      al === "RBT"
                        ? "RBT-"
                        : al === "BCBA" || al === "BCBA-D"
                          ? "1-"
                          : al === "BCABA"
                            ? "0-"
                            : "";
                    const s = String(value ?? "").trim();
                    if (!s && prefix) {
                      handleFieldChange(field.id, prefix);
                    }
                  }
                }}
                onChange={(e) => {
                  let v = String(e.target.value ?? "");
                  if (field.id === "applicantBACBID") {
                    v = v.toUpperCase();
                    const digits = v
                      .replace(/^BACB/i, "")
                      .replace(/\D+/g, "")
                      .slice(0, 6);
                    v = `BACB${digits}`;
                  } else if (field.id === "certification#") {
                    v = v.toUpperCase();
                    const analyst = formValues["analystLevel"] as any;
                    const al = String(
                      typeof analyst === "object"
                        ? (analyst?.text ?? analyst?.key ?? analyst?.value)
                        : (analyst ?? ""),
                    ).toUpperCase();
                    const prefix =
                      al === "RBT"
                        ? "RBT-"
                        : al === "BCBA" || al === "BCBA-D"
                          ? "1-"
                          : al === "BCABA"
                            ? "0-"
                            : "";
                    if (prefix) {
                      const afterPrefix = v.startsWith(prefix)
                        ? v.slice(prefix.length)
                        : v;
                      const digits = afterPrefix.replace(/\D+/g, "");
                      const part1 = digits.slice(0, 2);
                      const part2 = digits.slice(2);
                      v = prefix + part1 + (part2 ? "-" + part2 : "");
                    } else {
                      const digits = v.replace(/\D+/g, "");
                      v = digits;
                    }
                  }
                  handleFieldChange(field.id, v);
                }}
              />
            )}
          </FieldWrapper>
        );
      }

      case "List":
        return (
          <FieldWrapper
            key={field.id}
            fieldId={field.id}
            label={field.title}
            description={field.description}
            required={field.required}
            error={field.error}
          >
            <ListDropdown
              id={field.id}
              placeholder={field.placeholder}
              required={field.required}
              multi={field.multi}
              value={value}
              options={getOptionsForField(field)}
              onChange={(val) => handleFieldChange(field.id, val)}
            />
          </FieldWrapper>
        );

      case "Phone":
        return (
          <FieldWrapper
            key={field.id}
            fieldId={field.id}
            label={field.title}
            description={field.description}
            required={field.required}
            error={field.error}
          >
            <PhoneInput
              id={field.id}
              placeholder={field.placeholder}
              required={field.required}
              value={value}
              onChange={(val) => handleFieldChange(field.id, val)}
            />
          </FieldWrapper>
        );

      case "Date":
        return (
          <FieldWrapper
            key={field.id}
            fieldId={field.id}
            label={field.title}
            description={field.description}
            required={field.required}
            error={field.error}
          >
            <DateField
              id={field.id}
              placeholder={field.placeholder}
              value={value}
              minDays={field.mindays}
              maxDays={field.maxdays}
              onChange={(d) => handleFieldChange(field.id, d)}
            />
          </FieldWrapper>
        );

      case "Checkbox": {
        return (
          <FieldWrapper
            key={field.id}
            fieldId={field.id}
            label={field.title}
            description={field.description}
            required={field.required}
            error={field.error}
            layout="checkbox"
          >
            <input
              id={field.id}
              className="form-checkbox-input"
              type="checkbox"
              checked={toBool(value)}
              required={field.required}
              onChange={(e) => handleFieldChange(field.id, e.target.checked)}
            />
          </FieldWrapper>
        );
      }

      case "PeoplePicker":
        return (
          <FieldWrapper
            key={field.id}
            fieldId={field.id}
            label={field.title}
            description={field.description}
            required={field.required}
            error={field.error}
          >
            <PeoplePicker
              id={field.id}
              label={field.title}
              multi={field.multi}
              required={field.required}
              value={value}
              hideLabel
              onChange={(val) => {
                handleFieldChange(field.id, val);
                // Field mapping is handled by onPersonaSelected callback
              }}
              onPersonaSelected={(persona) => {
                // New callback to receive full persona data
                const mappings = (field as any).fieldMappings;
                if (persona && mappings) {
                  if (mappings.nameField && persona.name) {
                    handleFieldChange(mappings.nameField, persona.name);
                  }
                  if (mappings.emailField && persona.email) {
                    handleFieldChange(mappings.emailField, persona.email);
                  }
                }
              }}
              placeholder={field.placeholder}
            />
          </FieldWrapper>
        );

      case "PrioritizedList":
        return (
          <FieldWrapper
            key={field.id}
            fieldId={field.id}
            label={field.title}
            description={field.description}
            required={field.required}
            error={field.error}
          >
            <PrioritizedListPicker
              id={field.id}
              required={field.required}
              value={value}
              options={getOptionsForField(field)}
              onChange={(val) => handleFieldChange(field.id, val)}
              instructionText={(field as any).instructionText}
              listTagText={(field as any).listTagText}
            />
          </FieldWrapper>
        );

      default:
        return null;
    }
  };

  const onSaveClick = async () => {
    setMessage("");
    setLoading(true);
    // Yield to the browser so the spinner paints before the work starts
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
      const result = await handleSave(
        props.fields,
        formValues,
        siteUrl,
        user?.LoginName || "unknown",
        fieldOptions,
      );
      setMessageType(result.type);
      setMessage(result.message);
      setTimeout(() => {
        setMessage("");
      }, 5000);
      if (result.type === "success") {
        setReloading(true);
        setTimeout(() => {
          window.location.reload();
        }, 1200);
      }
    } catch (err: any) {
      setMessageType((err && err.type) || "error");
      setMessage(err?.message || "Error adding. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onUpdateClick = async () => {
    if (!user) {
      setMessageType("error");
      setMessage("User information is not available. Please try again.");
      return;
    }

    setMessage("");
    setLoading(true);
    // Yield to the browser so the spinner paints before the work starts
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
      const result = await handleUpdate(
        props.fields,
        formValues,
        user?.LoginName,
        props.itemId as number,
        siteUrl,
        fieldOptions,
      );
      setMessageType(result.type);
      setMessage(result.message);
      setTimeout(() => {
        setMessage("");
      }, 5000);
      if (result.type === "success") {
        setReloading(true);
        setTimeout(() => {
          window.location.reload();
        }, 1200);
      }
    } catch (err: any) {
      setMessageType((err && err.type) || "error");
      setMessage(err?.message || "Error updating profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="form-container">
        {(loading || isLoading || reloading) && <LoadingSpinner />}
        {message && (
          <MessageTile
            isVisible={!!message}
            message={message}
            type={messageType}
          />
        )}
        {!isLoading &&
          !loading &&
          !reloading &&
          fieldRows.map((row, rowIndex) => {
            if (row.length === 1 && row[0].type === "Label") {
              return (
                <div key={`row-${rowIndex}`} style={{ width: "100%" }}>
                  {renderField(row[0])}
                </div>
              );
            }
            return (
              <div
                key={`row-${rowIndex}`}
                className={`form-row form-row--cols-${effectiveColumns}`}
              >
                {row.map((f) => (
                  <React.Fragment key={f.id}>{renderField(f)}</React.Fragment>
                ))}
              </div>
            );
          })}
      </div>

      {props.mode === "save" && (
        <div style={buttonContainerStyle}>
          {props.onCancel && (
            <button
              type="button"
              className="btn-secondary"
              onClick={props.onCancel}
              style={{
                minWidth: "10vw",
                borderRadius: modernStyles.borderRadius,
              }}
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            className="btn-primary"
            onClick={onSaveClick}
            style={{
              minWidth: "10vw",
              borderRadius: modernStyles.borderRadius,
              background: modernStyles.backgroundColor,
            }}
          >
            Save
          </button>
        </div>
      )}

      {props.mode === "update" && (
        <div style={buttonContainerStyle}>
          {props.onCancel && (
            <button
              type="button"
              className="btn-secondary"
              onClick={props.onCancel}
              style={{
                minWidth: "10vw",
                borderRadius: modernStyles.borderRadius,
                border: "none",
              }}
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            className="btn-primary"
            onClick={onUpdateClick}
            disabled={loading || isLoading || reloading}
            style={{
              minWidth: "10vw",
              borderRadius: modernStyles.borderRadius,
              background: modernStyles.backgroundColor,
            }}
          >
            Update
          </button>
        </div>
      )}
    </>
  );
};

export default Form;
